import React, { useState, useEffect } from 'react';
import './eNoticeModule.css';
import NoticeList from './NoticeList';
import NoticeDetail from './NoticeDetail';
import NoticeReplyForm from './NoticeReplyForm';

const ENoticeModule = ({ currentUser, onBack, onTabChange }) => {
  const [notices, setNotices] = useState([]);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [currentView, setCurrentView] = useState('list'); // 'list', 'detail', 'reply'
  const [filter, setFilter] = useState('all'); // 'all', 'unsigned', 'signed'
  const [isLoading, setIsLoading] = useState(true);

  // Mock data for demonstration - in real app this would come from Supabase
  useEffect(() => {
    const mockNotices = [
      {
        id: 'N001',
        title: '水運會通告',
        titleEn: 'Swimming Gala Notice',
        content: '各位家長 本校將於下星期一,四月十五日舉行水運會,現邀請貴家長出席是次活動,地點:香港公園游泳池 時間:9:30am -12 30 pm',
        contentEn: 'Dear parents, our school will hold a swimming gala next Monday, April 15th. We invite you to attend this event. Location: Hong Kong Park Swimming Pool. Time: 9:30 am - 12:30 pm',
        publishDate: '2024-06-23',
        responseDeadline: '2024-06-27',
        status: 'published',
        priority: 'normal',
        category: 'event',
        targetAudience: ['1A', '1B', '1C'],
        actions: [
          {
            id: 'A001',
            type: 'attendance',
            title: '出席人數',
            titleEn: 'Attendance Count',
            required: true,
            options: ['1', '2', '3', '4或以上'],
            optionsEn: ['1', '2', '3', '4 or more']
          },
          {
            id: 'A002',
            type: 'feedback',
            title: '活動期望',
            titleEn: 'Activity Expectations',
            required: true,
            inputType: 'text'
          }
        ],
        studentResponses: [
          {
            studentId: '1A-47',
            studentName: '郭大寶',
            studentNameEn: 'Guo Dabao',
            signed: false,
            signedBy: null,
            signedAt: null,
            responses: {}
          }
        ]
      },
      {
        id: 'N002',
        title: '是否購買地界地圖集',
        titleEn: 'Do you want to buy a boundary map set?',
        content: '學校計劃為學生購買地界地圖集，請家長回覆是否同意購買。',
        contentEn: 'The school plans to purchase boundary map sets for students. Please reply whether you agree to the purchase.',
        publishDate: '2014-06-18',
        responseDeadline: '2014-06-25',
        status: 'expired',
        priority: 'low',
        category: 'purchase',
        targetAudience: ['all'],
        actions: [
          {
            id: 'A003',
            type: 'choice',
            title: '是否同意購買',
            titleEn: 'Agree to Purchase',
            required: true,
            options: ['同意', '不同意'],
            optionsEn: ['Agree', 'Disagree']
          }
        ],
        studentResponses: [
          {
            studentId: '1A-47',
            studentName: '郭大寶',
            studentNameEn: 'Guo Dabao',
            signed: true,
            signedBy: '郭大寶家長',
            signedAt: '2014-06-20 10:30:00',
            responses: { 'A003': '同意' }
          }
        ]
      },
      {
        id: 'N003',
        title: '暑期興趣班現正招生',
        titleEn: 'Summer Interest Classes Now Open for Enrollment',
        content: '暑期興趣班現正招生，包括音樂、美術、體育等課程。',
        contentEn: 'Summer interest classes are now open for enrollment, including music, art, sports and other courses.',
        publishDate: '2014-06-10',
        responseDeadline: '2014-06-20',
        status: 'expired',
        priority: 'medium',
        category: 'enrollment',
        targetAudience: ['all'],
        actions: [
          {
            id: 'A004',
            type: 'enrollment',
            title: '報名課程',
            titleEn: 'Enroll in Courses',
            required: false,
            options: ['音樂', '美術', '體育', '不參加'],
            optionsEn: ['Music', 'Art', 'Sports', 'Not Participating']
          }
        ],
        studentResponses: [
          {
            studentId: '1A-47',
            studentName: '郭大寶',
            studentNameEn: 'Guo Dabao',
            signed: true,
            signedBy: '郭大寶家長',
            signedAt: '2014-06-15 14:20:00',
            responses: { 'A004': '音樂' }
          }
        ]
      }
    ];

    setNotices(mockNotices);
    setIsLoading(false);
  }, []);

  const handleNoticeSelect = (notice) => {
    setSelectedNotice(notice);
    setCurrentView('detail');
  };

  const handleSignNotice = (notice) => {
    setSelectedNotice(notice);
    setCurrentView('reply');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedNotice(null);
  };

  const handleBackToDetail = () => {
    setCurrentView('detail');
  };

  const handleSubmitReply = (responses) => {
    // Update the notice with the responses
    const updatedNotices = notices.map(notice => {
      if (notice.id === selectedNotice.id) {
        return {
          ...notice,
          studentResponses: notice.studentResponses.map(response => {
            if (response.studentId === '1A-47') { // Assuming current user's child
              return {
                ...response,
                signed: true,
                signedBy: currentUser?.name || '家長',
                signedAt: new Date().toLocaleString('zh-HK'),
                responses: responses
              };
            }
            return response;
          })
        };
      }
      return notice;
    });

    setNotices(updatedNotices);
    setSelectedNotice(updatedNotices.find(n => n.id === selectedNotice.id));
    setCurrentView('detail');
  };

  const filteredNotices = notices.filter(notice => {
    if (filter === 'unsigned') {
      return notice.studentResponses.some(response => !response.signed);
    } else if (filter === 'signed') {
      return notice.studentResponses.every(response => response.signed);
    }
    return true;
  });

  if (isLoading) {
    return (
      <div className="enotice-loading">
        <div className="loading-spinner"></div>
        <p>載入電子通告中...</p>
      </div>
    );
  }

  return (
    <div className="enotice-module">
      <div className="enotice-header">
        <button className="back-button" onClick={onBack}>
          ← 返回
        </button>
        <h1>電子通告 (eNotice)</h1>
        <div className="header-actions">
          <span className="user-role">家長效果</span>
          <button className="search-button">🔍</button>
        </div>
      </div>

      {currentView === 'list' && (
        <NoticeList
          notices={filteredNotices}
          filter={filter}
          onFilterChange={setFilter}
          onNoticeSelect={handleNoticeSelect}
          onSignNotice={handleSignNotice}
        />
      )}

      {currentView === 'detail' && selectedNotice && (
        <NoticeDetail
          notice={selectedNotice}
          onBack={handleBackToList}
          onSign={handleSignNotice}
        />
      )}

      {currentView === 'reply' && selectedNotice && (
        <NoticeReplyForm
          notice={selectedNotice}
          onBack={handleBackToDetail}
          onSubmit={handleSubmitReply}
        />
      )}
    </div>
  );
};

export default ENoticeModule;
